"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpService = void 0;
/* eslint-disable no-undef */
const mystake_auth_frontend_sdk_1 = require("@redbelly-tokeniser/mystake-auth-frontend-sdk");
/* eslint-disable @typescript-eslint/no-explicit-any */
const axios_1 = __importDefault(require("axios"));
const sessionIdStorage_1 = require("./sessionIdStorage");
const avererBffSessionStorage_1 = require("./avererBffSessionStorage");
const lastCorrelationId_1 = require("./lastCorrelationId");
var HttpMethod;
(function (HttpMethod) {
    HttpMethod["get"] = "GET";
    HttpMethod["post"] = "POST";
    HttpMethod["patch"] = "PATCH";
    HttpMethod["put"] = "PUT";
    HttpMethod["delete"] = "DELETE";
})(HttpMethod || (HttpMethod = {}));
/** a configured axios instance that can be used to interact with api */
class HttpService {
    constructor(firebaseAuthConfig, baseUrl, webBaseUrl) {
        this.baseUrl = baseUrl;
        this.webBaseUrl = webBaseUrl;
        this.axiosInstance = axios_1.default.create({
            baseURL: this.baseUrl,
            // Send the first-party Averer OIDC session cookie (HttpOnly) on every request so
            // BFF-authenticated users are recognised without holding a bearer token in the browser.
            withCredentials: true,
            // Resolve the promise for any HTTP status. The API's error envelope
            // ({ status, message, correlationId, payload }) is uniform across 4xx and 5xx,
            // so callers unwrap the same shape whether the server responded 400 or 500.
            // Throwing on 5xx would give consumers an AxiosError with undefined body and
            // silently break every service method that reads `.payload` on failure.
            validateStatus: () => true,
        });
        const captureCorrelationId = (headers) => {
            const correlationId = headers?.["x-request-id"];
            if (typeof correlationId === "string" && correlationId.length > 0) {
                (0, lastCorrelationId_1.setLastCorrelationId)(correlationId);
            }
        };
        this.axiosInstance.interceptors.response.use((response) => {
            captureCorrelationId(response.headers);
            return response;
        }, (error) => {
            // Network-layer failures (no response) skip this branch entirely, but any
            // response the server actually returned still carries a correlation id we
            // want stored before the promise rejects.
            captureCorrelationId(error?.response?.headers);
            return Promise.reject(error);
        });
        this.firebaseInstance = mystake_auth_frontend_sdk_1.FirebaseAuthService.getInstance(firebaseAuthConfig);
    }
    static getInstance({ baseUrl, webBaseUrl }) {
        if (!HttpService.instance) {
            const firebaseAuthConfig = window?.config?.FIREBASE_AUTH_CONFIG || {};
            HttpService.instance = new HttpService(firebaseAuthConfig, baseUrl, webBaseUrl);
        }
        return HttpService.instance;
    }
    async get(url, data, headers) {
        const response = await this.axiosHttpFunction(url, HttpMethod.get, data, headers);
        return response;
    }
    async post(url, data, headers) {
        const response = await this.axiosHttpFunction(url, HttpMethod.post, data, headers);
        return response;
    }
    async patch(url, data, headers) {
        const response = await this.axiosHttpFunction(url, HttpMethod.patch, data, headers);
        return response;
    }
    async put(url, data, headers) {
        const response = await this.axiosHttpFunction(url, HttpMethod.put, data, headers);
        return response;
    }
    async delete(url, data, headers) {
        const response = await this.axiosHttpFunction(url, HttpMethod.delete, data, headers);
        return response;
    }
    /**
     * Firebase ID token for bearer auth. Averer users authenticate via the HttpOnly
     * session cookie (sent automatically via withCredentials), not a browser-held token.
     * When an Averer BFF session is active, skip the Firebase bearer so it cannot shadow
     * the cookie on the server (Authorization is checked before cookies).
     */
    async resolveAuthToken() {
        if ((0, avererBffSessionStorage_1.isAvererBffSessionActive)()) {
            return null;
        }
        return this.firebaseInstance.getIdToken();
    }
    async signOutActiveProvider() {
        await this.firebaseInstance.signOut();
    }
    async axiosHttpFunction(url, method, data, customHeaders) {
        const authToken = await this.resolveAuthToken();
        const headers = {
            "session-id": (0, sessionIdStorage_1.getCurrentSessionId)(),
            ...customHeaders,
        };
        // Only send a bearer token when we actually have one (Firebase). Averer BFF users
        // rely on the session cookie, so we must not send "Bearer null".
        if (authToken) {
            headers.authorization = `Bearer ${authToken}`;
        }
        try {
            const response = await this.axiosInstance({
                url,
                method,
                data,
                headers,
            });
            return response;
        }
        catch (error) {
            if (error.response) {
                if (error?.response.status === 401) {
                    await this.signOutActiveProvider();
                    window.location.href = `${this.webBaseUrl}/signin`;
                }
            }
            return error;
        }
    }
}
exports.HttpService = HttpService;
//# sourceMappingURL=HttpService.js.map